/**
 * Intake Completion Modal
 *
 * Cash App-grade modal shown when an intake session ends successfully.
 * Features:
 * - Animated success state with confetti-style celebration
 * - CSAT survey with emoji rating (PostHog integration)
 * - Clean dismissal with callback
 */

import { AnimatePresence,motion } from 'framer-motion';
import { CheckCircle2, Sparkles } from 'lucide-react';
import { usePostHog } from 'posthog-js/react';
import React, { useEffect, useState } from 'react';

import { POSTHOG_SURVEYS } from '@/constants/analytics/posthog-surveys';
import { cn } from '@/lib/utils';

import { FirstContactOutcome } from './types';

const SURVEY = POSTHOG_SURVEYS.INTAKE_V3_CSAT;
const QUESTION = SURVEY.questions[0];

// Emoji ratings matching the survey config
const EMOJI_RATINGS = [
  { value: 1, emoji: '😠', label: 'Very dissatisfied' },
  { value: 2, emoji: '😕', label: 'Dissatisfied' },
  { value: 3, emoji: '😐', label: 'Neutral' },
  { value: 4, emoji: '🙂', label: 'Satisfied' },
  { value: 5, emoji: '😍', label: 'Very satisfied' },
];

interface IntakeCompletionModalProps {
  open: boolean;
  outcome: FirstContactOutcome;
  clientName?: string;
  onClose: () => void;
}

export const IntakeCompletionModal: React.FC<IntakeCompletionModalProps> = ({
  open,
  outcome,
  clientName,
  onClose,
}) => {
  const posthog = usePostHog();
  const [selectedRating, setSelectedRating] = useState<number | null>(null);
  const [isClosing, setIsClosing] = useState(false);

  // Track survey shown when modal opens
  useEffect(() => {
    if (open) {
      posthog.capture('survey shown', {
        $survey_id: SURVEY.id,
      });
    }
  }, [open, posthog]);

  const handleRatingClick = (rating: number) => {
    setSelectedRating(rating);

    // Capture as custom event
    posthog.capture('intake_csat_response', {
      survey_id: SURVEY.id,
      question_id: QUESTION.id,
      question: QUESTION.question,
      rating,
      rating_label: EMOJI_RATINGS.find((r) => r.value === rating)?.label,
      outcome,
    });

    // Also capture as survey sent for PostHog survey analytics
    posthog.capture('survey sent', {
      $survey_id: SURVEY.id,
      $survey_questions: [
        {
          id: QUESTION.id,
          question: QUESTION.question,
        },
      ],
      [`$survey_response_${QUESTION.id}`]: rating.toString(),
    });

    // Auto-dismiss after a brief delay to show selection
    setTimeout(() => {
      handleClose();
    }, 600);
  };

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      onClose();
      setIsClosing(false);
      setSelectedRating(null);
    }, 200);
  };

  // Get outcome-specific messaging
  const getOutcomeContent = () => {
    switch (outcome) {
      case 'completed':
        return {
          icon: <CheckCircle2 className="w-12 h-12 text-emerald-500" />,
          title: 'Intake Complete',
          subtitle: clientName ? `${clientName} is all set!` : 'Great work!',
          color: 'emerald',
        };
      case 'incomplete':
        return {
          icon: <Sparkles className="w-12 h-12 text-amber-500" />,
          title: 'Session Saved',
          subtitle: 'You can pick up where you left off',
          color: 'amber',
        };
      case 'unavailable':
        return {
          icon: <Sparkles className="w-12 h-12 text-zinc-400" />,
          title: 'Marked Unavailable',
          subtitle: 'Client info has been saved',
          color: 'zinc',
        };
      default:
        return {
          icon: <CheckCircle2 className="w-12 h-12 text-emerald-500" />,
          title: 'Done',
          subtitle: '',
          color: 'emerald',
        };
    }
  };

  const content = getOutcomeContent();

  if (!open) return null;

  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
            onClick={handleClose}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: isClosing ? 0 : 1, scale: isClosing ? 0.9 : 1, y: isClosing ? 20 : 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ type: 'spring', stiffness: 400, damping: 30 }}
            className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-full max-w-sm"
          >
            <div className="bg-zinc-900 rounded-3xl shadow-2xl overflow-hidden border border-zinc-800">
              {/* Success Header */}
              <div className="pt-8 pb-6 px-6 text-center">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: 'spring', stiffness: 400, damping: 20, delay: 0.1 }}
                  className="inline-flex items-center justify-center mb-4"
                >
                  {content.icon}
                </motion.div>

                <motion.h2
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.15 }}
                  className="text-xl font-black text-white mb-1"
                >
                  {content.title}
                </motion.h2>

                {content.subtitle && (
                  <motion.p
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="text-sm font-medium text-zinc-400"
                  >
                    {content.subtitle}
                  </motion.p>
                )}
              </div>

              {/* CSAT Survey */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.25 }}
                className="px-6 pb-6"
              >
                <div className="bg-zinc-800/50 rounded-2xl p-4">
                  <p className="text-xs font-bold text-zinc-400 uppercase tracking-wider text-center mb-4">
                    {QUESTION.question}
                  </p>

                  {/* Emoji rating buttons */}
                  <div className="flex justify-center gap-2">
                    {EMOJI_RATINGS.map(({ value, emoji, label }) => (
                      <motion.button
                        key={value}
                        type="button"
                        onClick={() => handleRatingClick(value)}
                        disabled={selectedRating !== null}
                        className={cn(
                          'w-12 h-12 rounded-xl flex items-center justify-center text-2xl transition-all',
                          selectedRating === value
                            ? 'bg-white scale-110'
                            : selectedRating !== null
                              ? 'opacity-30 cursor-not-allowed'
                              : 'bg-zinc-700/50 hover:bg-zinc-700 hover:scale-105 active:scale-95'
                        )}
                        whileHover={selectedRating === null ? { scale: 1.1 } : {}}
                        whileTap={selectedRating === null ? { scale: 0.95 } : {}}
                        aria-label={label}
                      >
                        {emoji}
                      </motion.button>
                    ))}
                  </div>

                  {/* Labels */}
                  <div className="flex justify-between text-[10px] text-zinc-500 mt-3 px-1">
                    <span>{QUESTION.lowerBoundLabel}</span>
                    <span>{QUESTION.upperBoundLabel}</span>
                  </div>
                </div>
              </motion.div>

            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default IntakeCompletionModal;
